import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { enqueueCartMutation } from '@theme/cart-items';

/**
 * Required add-ons (PP-988)
 *
 * Products that carry the tag of a rule must ship with a free add-on, for example
 * the power adapter for Swiss sockets. This module keeps the add-on lines of the
 * cart in step with the products that need them:
 *
 * - The server decides. snippets/required-addons.liquid compares the cart with
 *   the rules and renders the quantities the add-on lines must get as a JSON
 *   payload. The payload is part of every cart markup (drawer and cart page) and
 *   of the small section `required-addons-sync`.
 * - Every cart change of the theme carries fresh cart markup. The payload in it
 *   tells whether an add-on is missing, has the wrong quantity or is left over.
 *   If so, ONE cart update sets the add-on lines to the required quantities.
 *   This covers every way a product gets into the cart (product page, product
 *   cards, search, upsell pop-up, undo) without touching those flows.
 * - Before the customer enters the checkout the live cart is checked once more,
 *   so carts that were changed without a theme event are corrected as well.
 *
 * All requests run through the cart mutation queue of the cart items component,
 * so they never overlap with quantity changes or removals by the customer.
 */

/**
 * @typedef {object} SyncPayload
 * @property {Record<string, number>} updates - Line item key or variant id with the
 *   quantity the add-on must get.
 */

const SYNC_SOURCE = 'required-addons-sync';
const SYNC_SECTION = 'required-addons-sync';
const PAYLOAD_SELECTOR = 'script[data-required-addons-sync]';
const PAYLOAD_PATTERN = /<script[^>]*\sdata-required-addons-sync[^>]*>([\s\S]*?)<\/script>/g;
const CHECKOUT_TRIGGER_SELECTOR =
  'button[name="checkout"], input[name="checkout"], a[data-checkout], a[href$="/checkout"]';

// A cart update can lead to a follow-up correction (the payload of the response
// is checked again). The cap keeps a rule that can never be fulfilled, for
// example an add-on that is not available in the store, from looping.
const MAX_SYNC_PASSES = 3;
// The checkout never waits longer than this for the final check.
const CHECKOUT_CHECK_TIMEOUT = 3000;
const LIVE_CHECK_DELAY = 300;
// No request of this module may hang forever: it would block the cart mutation
// queue for every later cart change on the page.
const REQUEST_TIMEOUT = 8000;

// While a correction runs, clicks in the cart are held back (see handleCartClick)
// and the add-on lines the correction removes are hidden right away.
const UPDATING_ATTRIBUTE = 'data-required-addons-updating';
const REMOVING_CLASS = 'cart-items__table-row--required-addon-removing';
const ADDON_ROW_SELECTOR = '.cart-items__table-row--required-addon';
const CART_AREA_SELECTOR =
  'cart-items-component, cart-drawer-component, .cart-summary, .cart-drawer__summary, [id^="omgrfq"], [class*="omgrfq"]';
const HELD_CLICK_SELECTOR = 'button, a[href], input[type="submit"], [role="button"]';
// The theme's own line controls run through its mutation queue and are never held.
const HELD_CLICK_EXCLUDE_SELECTOR = '.close-button, [class*="close-button"], [on\\:click]';
// Line controls of the theme: as soon as one is used, a cart change is in flight.
const CART_CHANGE_TRIGGER_SELECTOR =
  'cart-items-component .cart-items__remove, cart-items-component .quantity-minus, cart-items-component .quantity-plus, cart-items-component [on\\:click*="LineItemRemove"], cart-items-component [on\\:click*="Quantity"]';
const CART_CHANGE_INPUT_SELECTOR = 'cart-items-component input';
// A cart change that never reports back ends the updating state on its own.
const CHANGE_TIMEOUT = 5000;
// Apps put their buttons back a moment after the cart markup was replaced.
const TRIGGER_RETRY_TIMEOUT = 1500;
const TRIGGER_RETRY_INTERVAL = 100;

/** @type {Promise<unknown> | null} */
let runningSync = null;
let liveCheckTimeout = 0;
let heldClickRunning = false;
let pendingCorrection = false;
let markLoopFrame = 0;
let changeTimeout = 0;
/** @type {Promise<void> | null} resolves when the running correction state ends. */
let correctionDone = null;
/** @type {(() => void) | null} */
let resolveCorrection = null;
/** Line item keys and variant ids of the add-on lines the running correction removes. */
const removingKeys = new Set();

/**
 * @param {SyncPayload | null | undefined} payload
 * @returns {boolean}
 */
function hasUpdates(payload) {
  return !!payload && Object.keys(payload.updates).length > 0;
}

/**
 * Reads the sync payload from rendered cart markup.
 *
 * @param {string} html
 * @returns {SyncPayload | null} null when the markup carries no payload at all.
 */
function readPayload(html) {
  /** @type {SyncPayload | null} */
  let payload = null;

  for (const match of html.matchAll(PAYLOAD_PATTERN)) {
    try {
      const parsed = JSON.parse(match[1] || '');
      if (!parsed || typeof parsed.updates !== 'object' || parsed.updates === null) continue;
      payload = { updates: parsed.updates };
      if (hasUpdates(payload)) return payload;
    } catch {
      // Unreadable payload: treated like missing markup, the live check decides.
    }
  }

  return payload;
}

/**
 * @param {Record<string, string> | null | undefined} sections
 * @returns {SyncPayload | null}
 */
function readPayloadFromSections(sections) {
  if (!sections) return null;

  /** @type {SyncPayload | null} */
  let payload = null;

  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const sectionPayload = readPayload(html);
    if (hasUpdates(sectionPayload)) return sectionPayload;
    payload = sectionPayload ?? payload;
  }

  return payload;
}

/**
 * @param {string} url
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
function fetchWithTimeout(url, init = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timeout));
}

/**
 * URL of the sync section, rendered for the live cart. The Section Rendering API
 * answers on any page URL, so the root of the current locale is used. The cart
 * URL is not usable here: asked for JSON, `/cart` returns the cart object and
 * ignores the `sections` parameter.
 *
 * @returns {string}
 */
function getSyncSectionUrl() {
  const root = Theme.routes.root_url_prefix || window.Shopify?.routes?.root || '/';
  return `${root}?sections=${SYNC_SECTION}`;
}

/**
 * Asks the server for the payload of the live cart.
 *
 * @returns {Promise<SyncPayload | null>}
 */
async function fetchLivePayload() {
  try {
    const response = await fetchWithTimeout(getSyncSectionUrl(), {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return readPayloadFromSections(await response.json());
  } catch {
    return null;
  }
}

/** @returns {string[]} The sections that show the cart on this page. */
function getCartSectionIds() {
  const sectionIds = new Set();

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.add(component.dataset.sectionId);
    }
  });
  // The header section hosts the cart icon with count and subtotal.
  const headerSection = document.querySelector('cart-icon')?.closest('.shopify-section');
  if (headerSection?.id) {
    sectionIds.add(headerSection.id.replace('shopify-section-', ''));
  }

  return Array.from(sectionIds);
}

/**
 * Sets the add-on lines to the given quantities and hands the fresh cart markup
 * to the cart components.
 *
 * @param {Record<string, number>} updates
 * @returns {Promise<SyncPayload | null>} Payload of the cart after the update.
 */
async function applyUpdates(updates) {
  const response = await fetchWithTimeout(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      updates,
      sections: getCartSectionIds().join(','),
      sections_url: window.location.pathname,
    }),
  });
  if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);

  const cart = await response.json();

  document.dispatchEvent(
    new CartUpdateEvent(cart, SYNC_SOURCE, {
      itemCount: cart.item_count ?? 0,
      source: SYNC_SOURCE,
      sections: cart.sections,
    })
  );

  return readPayloadFromSections(cart.sections);
}

/**
 * Marks the start of a correction: the page shows the cart as updating, and the
 * add-on lines that are about to be removed disappear at once instead of being
 * shown (and usable) until the corrected cart markup arrives.
 *
 * @param {Record<string, number>} updates
 */
function noteCorrection(updates) {
  for (const [key, quantity] of Object.entries(updates)) {
    if (Number(quantity) === 0) removingKeys.add(String(key));
  }
  if (pendingCorrection) return;
  pendingCorrection = true;
  correctionDone = new Promise((resolve) => {
    resolveCorrection = resolve;
  });
  document.documentElement.setAttribute(UPDATING_ATTRIBUTE, '');
  applyRemovalMarks();
}

/** @returns {Promise<void>} resolves once no correction state is running. */
function correctionSettled() {
  return correctionDone ?? Promise.resolve();
}

/**
 * Hides the add-on lines the running correction removes. The theme morphs the
 * cart markup after its own update, which can drop the class again, so it is
 * re-applied every frame until the correction is through.
 */
function applyRemovalMarks() {
  cancelAnimationFrame(markLoopFrame);
  if (!pendingCorrection) return;

  if (removingKeys.size > 0) {
    document.querySelectorAll(ADDON_ROW_SELECTOR).forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      if (
        removingKeys.has(row.dataset.key ?? '') ||
        removingKeys.has(row.dataset.variantId ?? '')
      ) {
        row.classList.add(REMOVING_CLASS);
      }
    });
  }

  markLoopFrame = requestAnimationFrame(applyRemovalMarks);
}

/**
 * A cart change of the theme starts (remove, quantity). Its result decides
 * whether the add-on lines change, and the quote and leasing requests read the
 * cart in between: until the result is in, the add-on lines count as updating
 * and clicks in the cart wait. Only while the cart holds add-on lines at all.
 */
function beginChange() {
  if (!document.querySelector(ADDON_ROW_SELECTOR)) return;
  noteCorrection({});
  clearTimeout(changeTimeout);
  changeTimeout = setTimeout(() => {
    if (!runningSync) endCorrection();
  }, CHANGE_TIMEOUT);
}

/** @param {Event} event */
function handleCartChangeClick(event) {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest(CART_CHANGE_TRIGGER_SELECTOR)) return;
  beginChange();
}

/** @param {Event} event */
function handleCartChangeInput(event) {
  if (!(event.target instanceof Element) || !event.target.matches(CART_CHANGE_INPUT_SELECTOR))
    return;
  beginChange();
}

/** Ends the correction state once the last running sync is through. */
function endCorrection() {
  clearTimeout(changeTimeout);
  changeTimeout = 0;
  pendingCorrection = false;
  removingKeys.clear();
  cancelAnimationFrame(markLoopFrame);
  markLoopFrame = 0;
  document.documentElement.removeAttribute(UPDATING_ATTRIBUTE);
  document
    .querySelectorAll(`.${REMOVING_CLASS}`)
    .forEach((row) => row.classList.remove(REMOVING_CLASS));
  const resolve = resolveCorrection;
  correctionDone = null;
  resolveCorrection = null;
  resolve?.();
}

/**
 * Brings the add-on lines in step with the cart.
 *
 * @param {SyncPayload | null} payload - Payload that triggered the sync.
 * @param {object} [options]
 * @param {boolean} [options.verify] - Check the payload against the live cart
 *   first. Used for markup that may be older than the cart (page HTML).
 * @returns {Promise<unknown>} resolves when the cart is in step (or the sync gave up).
 */
function syncAddons(payload, { verify = false } = {}) {
  if (!verify && !hasUpdates(payload)) return runningSync ?? Promise.resolve();
  if (hasUpdates(payload)) noteCorrection(/** @type {SyncPayload} */ (payload).updates);

  const previousSync = runningSync ?? Promise.resolve();
  const sync = previousSync
    .then(() =>
      enqueueCartMutation(async () => {
        let current = verify ? await fetchLivePayload() : payload;
        if (hasUpdates(current)) noteCorrection(/** @type {SyncPayload} */ (current).updates);

        for (let pass = 0; pass < MAX_SYNC_PASSES && hasUpdates(current); pass++) {
          try {
            current = await applyUpdates(/** @type {SyncPayload} */ (current).updates);
          } catch (error) {
            // The payload was older than the cart (a line it names is gone): the
            // live cart tells what is still needed.
            console.error('Required add-ons sync failed:', error);
            current = await fetchLivePayload();
          }
        }
      })
    )
    .catch((error) => {
      console.error('Required add-ons sync failed:', error);
    })
    .finally(() => {
      if (runningSync === sync) {
        runningSync = null;
        endCorrection();
      }
    });

  runningSync = sync;
  return sync;
}

/** Checks the live cart shortly after a cart change that came without markup. */
function scheduleLiveCheck() {
  clearTimeout(liveCheckTimeout);
  liveCheckTimeout = setTimeout(() => {
    syncAddons(null, { verify: true });
  }, LIVE_CHECK_DELAY);
}

/**
 * @param {Event} event
 */
function handleCartUpdate(event) {
  const data = /** @type {any} */ (event).detail?.data;
  if (data?.source === SYNC_SOURCE) return;

  const payload = readPayloadFromSections(data?.sections);
  if (payload) {
    if (hasUpdates(payload)) {
      syncAddons(payload);
    } else if (!runningSync) {
      // The change needed no correction: the cart is in step again.
      endCorrection();
    }
    return;
  }

  // Cart changes without cart markup (for example the refresh after returning
  // from the checkout): the server is asked instead.
  scheduleLiveCheck();
}

/**
 * Clicks in the cart wait for the add-on lines. The checkout is held back until
 * the live cart was checked; every other click in the cart (quote request,
 * leasing link, quantity buttons) is held back only while a correction runs.
 * Then the click is repeated on the element, or on the one that took its place
 * after a re-render. Nothing is blocked for good: after the timeout or on any
 * error the click goes through as it is.
 *
 * @param {MouseEvent} event
 */
function handleCartClick(event) {
  if (!(event.target instanceof Element)) return;

  const repeated = event.target.closest('[data-required-addons-checked="true"]');
  if (repeated instanceof HTMLElement) {
    delete repeated.dataset.requiredAddonsChecked;
    return;
  }

  const checkoutTrigger = event.target.closest(CHECKOUT_TRIGGER_SELECTOR);
  if (checkoutTrigger instanceof HTMLElement) {
    holdClick(event, checkoutTrigger, () =>
      Promise.all([correctionSettled(), syncAddons(null, { verify: true })])
    );
    return;
  }

  if (!pendingCorrection) return;
  const trigger = event.target.closest(HELD_CLICK_SELECTOR);
  if (!(trigger instanceof HTMLElement) || !trigger.closest(CART_AREA_SELECTOR)) return;
  if (trigger.matches(HELD_CLICK_EXCLUDE_SELECTOR)) return;
  holdClick(event, trigger, correctionSettled);
}

/**
 * @param {MouseEvent} event
 * @param {HTMLElement} trigger
 * @param {() => Promise<unknown>} wait - resolves when the click may go through.
 */
function holdClick(event, trigger, wait) {
  event.preventDefault();
  event.stopImmediatePropagation();

  // Impatient second click while the first one waits: the first one still goes through.
  if (heldClickRunning) return;
  heldClickRunning = true;

  const timeout = new Promise((resolve) => setTimeout(resolve, CHECKOUT_CHECK_TIMEOUT));

  Promise.race([wait(), timeout]).finally(async () => {
    heldClickRunning = false;

    const target = await findConnectedTrigger(trigger);
    if (!target) return;
    target.dataset.requiredAddonsChecked = 'true';
    target.click();
  });
}

/**
 * A correction re-renders the cart, which can replace the clicked element, and
 * apps (quote request) put their buttons back a moment later. The click is then
 * repeated on the element that took its place: same tag, same classes, same
 * text. The search is retried for a short while before the click is dropped.
 *
 * @param {HTMLElement} trigger
 * @returns {Promise<HTMLElement | null>}
 */
async function findConnectedTrigger(trigger) {
  const text = (trigger.textContent ?? '').trim();
  const find = () => {
    if (trigger.isConnected) return trigger;
    const candidates = Array.from(document.querySelectorAll(trigger.tagName)).filter(
      (element) =>
        element instanceof HTMLElement &&
        element.className === trigger.className &&
        (element.textContent ?? '').trim() === text
    );
    const visible = candidates.find(
      (element) => element instanceof HTMLElement && element.offsetParent !== null
    );
    return /** @type {HTMLElement | null} */ (visible ?? candidates[0] ?? null);
  };

  const deadline = Date.now() + TRIGGER_RETRY_TIMEOUT;
  let target = find();
  while (!target && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, TRIGGER_RETRY_INTERVAL));
    target = find();
  }
  return target;
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
document.addEventListener('click', handleCartClick, true);
document.addEventListener('click', handleCartChangeClick, true);
document.addEventListener('change', handleCartChangeInput, true);

// Page HTML can be older than the cart (cached pages, carts changed in another
// tab), so a payload found in it is checked against the live cart first.
const renderedPayload = Array.from(document.querySelectorAll(PAYLOAD_SELECTOR))
  .map((element) => readPayload(element.outerHTML))
  .find((payload) => hasUpdates(payload));
if (renderedPayload) {
  syncAddons(null, { verify: true });
}
